import "./Customer.scss";

import { Col, Form, Row } from "react-bootstrap";
import {
  faAdd,
  faArrowUp,
  faBirthdayCake,
  faFileExcel,
  faSearch,
  faTimes,
  faUserGroup
} from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useRef, useState } from "react";

import AddManualCustomerModal from "./components/AddManualCustomerModal";
import AppButton from "../../commons/buttons/AppButton";
import AppInput from "../../commons/inputs/AppInput";
import AppLoading from "../../commons/AppLoading";
import ConfirmModal from "../../commons/modal/ConfirmModal";
import CustomerAPI from "./CustomerAPI";
import CustomerCard from "./components/CustomerCard";
import CustomerDetailsModal from "./components/CustomerDetailsModal";
import CustomerDetailsModalV2 from "./components/details/CustomerDetailsModalV2";
import EmptyState from "../../commons/EmptyState";
import FloatingButton from "../../commons/buttons/FloatingButton";
import FloatingVideoButton from "../../commons/buttons/FloatingVideoButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HallAppToast from "../../commons/HallAppToast";
import ImportCustomerModal from "./components/ImportCustomerModal";
import MenuTitle from "../../commons/menu/MenuTitle";

const customerAPI = new CustomerAPI();

function Customers() {
  const [customers, setCustomers] = useState([]);

  const [isLoadingFirst, setIsLoadingFirst] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState({});

  const [modalHistoryCustomerShow, setModalHistoryCustomerShow] = useState(false);

  const [showModalAddCustomer, setShowModalAddCustomer] = useState(false);
  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);

  const [showModalImportCustomer, setShowModalImportCustomer] = useState(false);
  const [birthdayMonth, setBirthdayMonth] = useState(false);

  // ===== paginação =====
  const [page, setPage] = useState(0);
  const limit = 20;

  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const hasMore = useMemo(() => {
    if (!totalPages) return false;
    return page < totalPages - 1;
  }, [page, totalPages]);

  // ===== pesquisa (controlada + botão) =====
  const [searchValue, setSearchValue] = useState("");
  const [query, setQuery] = useState("");

  // ===== botão "voltar ao topo" =====
  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  // ===== sentinel infinite scroll =====
  const sentinelRef = useRef(null);
  const observerRef = useRef(null);

  const loadingRef = useRef(false);
  useEffect(() => {
    loadingRef.current = isLoadingFirst || isLoadingMore;
  }, [isLoadingFirst, isLoadingMore]);

  useEffect(() => {
    resetAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, birthdayMonth]);

  async function resetAndLoad() {
    setCustomers([]);
    setTotalElements(0);
    setTotalPages(0);
    setPage(0);

    await loadPage({ pageToLoad: 0, mode: "replace" });
  }

  async function loadPage({ pageToLoad, mode }) {
    const isFirst = pageToLoad === 0 && mode === "replace";
    try {
      if (isFirst) setIsLoadingFirst(true);
      else setIsLoadingMore(true);

      const resp = await customerAPI.getCustomersPaginated({
        page: pageToLoad,
        limit,
        requestLastVisit: true,
        q: query,
        birthdayMonth: birthdayMonth ?? undefined,
      });

      const data = resp?.data || {};
      const content = Array.isArray(data.content) ? data.content : [];

      setTotalElements(Number(data.totalElements ?? 0));
      setTotalPages(Number(data.totalPages ?? 0));
      setPage(Number(data.number ?? pageToLoad));

      setCustomers((prev) => {
        if (mode === "replace") return content;

        const existingIds = new Set(prev.map((c) => c.id));
        const next = content.filter((c) => !existingIds.has(c.id));
        return [...prev, ...next];
      });
    } catch (e) {
      HallAppToast.genericError();
    } finally {
      if (isFirst) setIsLoadingFirst(false);
      else setIsLoadingMore(false);
    }
  }

  async function loadMore() {
    if (!hasMore) return;
    if (loadingRef.current) return;

    const nextPage = page + 1;
    await loadPage({ pageToLoad: nextPage, mode: "append" });
  }

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;
        if (hasMore && !loadingRef.current) loadMore();
      },
      { root: null, rootMargin: "200px", threshold: 0.01 }
    );

    observerRef.current.observe(el);
    return () => observerRef.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, page]);

  const customersLabel = useMemo(() => {
    if (totalElements === 0) return "Nenhum cliente cadastrado";
    if (totalElements === 1) return "1 cliente cadastrado";
    return `${totalElements} clientes cadastrados`;
  }, [totalElements]);

  function handleSearchClick() {
    setQuery((searchValue || "").trim());
    scrollToTop();
  }

  function handleClearSearch() {
    setSearchValue("");
    setQuery("");
    scrollToTop();
    setBirthdayMonth(false);
  }

  async function executeDeletion() {
    setIsLoadingModalDelete(true);
    try {
      await customerAPI.deleteCustomer(selectedCustomer.id);
      setShowModalDelete(false);
      setSelectedCustomer({});
      await resetAndLoad();
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
    }
  }

  const showEmpty = !isLoadingFirst && customers.length === 0;

  return (
    <div className="container py-4">
      <MenuTitle title="Clientes" showArrowInfo={false} />

      <div className="d-flex justify-content-center mt-2">
        <button
          type="button"
          className="btn btn-sm hallapp-chip"
          onClick={() => {
            setBirthdayMonth(true);
            scrollToTop();
          }}
        >
          <FontAwesomeIcon icon={faBirthdayCake} className="me-2" />
          Aniversariantes do mês
        </button>
      </div>

      <hr />

      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-muted">{customersLabel}</small>
        <AppButton onClick={() => setShowModalImportCustomer(true)} name="Importar clientes" />
      </div>

      <br />

      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearchClick();
        }}
      >
        <div className="customer-search">
          <AppInput
            label="Pesquisar (nome ou telefone)"
            name="search"
            errors={[]}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />

          <div className="customer-search-actions">
            <button type="submit" className="hallapp-search-btn" disabled={isLoadingFirst}>
              {isLoadingFirst ? (
                <span className="hallapp-btn-spinner" aria-hidden="true" />
              ) : (
                <FontAwesomeIcon icon={faSearch} />
              )}
              <span>Pesquisar</span>
            </button>

            {(query || searchValue) && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="hallapp-clear-btn"
                disabled={isLoadingFirst}
                aria-label="Limpar pesquisa"
                title="Limpar"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
        </div>
      </Form>

      {!!query && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
          Filtrando por: <span style={{ fontWeight: 600 }}>{query}</span>
        </div>
      )}

      {birthdayMonth && (
        <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280", textAlign: "center" }}>
          Filtrando: <span style={{ fontWeight: 700 }}>Aniversariantes do mês</span>
          <button
            type="button"
            className="btn btn-link p-0 ms-2"
            onClick={() => setBirthdayMonth(null)}
            style={{ fontSize: 12 }}
          >
            limpar
          </button>
        </div>
      )}

      <hr />

      <Row style={{ margin: 0, width: "100%" }}>
        {isLoadingFirst ? (
          <AppLoading />
        ) : showEmpty ? (
          <EmptyState
            title={query ? "Nenhum cliente encontrado" : "Nenhum cliente cadastrado"}
            description={
              query
                ? "Tente pesquisar com outro nome ou número."
                : "Cadastre clientes para agilizar agendamentos, histórico e campanhas. Você pode começar com um cadastro rápido ou importar uma planilha."
            }
            icon={<FontAwesomeIcon icon={faUserGroup} />}
            actionLabel="Cadastrar cliente"
            onAction={() => setShowModalAddCustomer(true)}
            secondaryActionLabel="Importar planilha"
            onSecondaryAction={() => setShowModalImportCustomer(true)}
            secondaryIcon={<FontAwesomeIcon icon={faFileExcel} />}
          />
        ) : (
          customers.map((c) => (
            <Col xs={12} sm={12} md={4} lg={4} key={c.id} style={{ marginBottom: "10px" }}>
              <CustomerCard
                customer={c}
                setModalHistoryCustomerShow={setModalHistoryCustomerShow}
                setSelectedCustomer={setSelectedCustomer}
                onEdit={() => {
                  setSelectedCustomer(c);
                  setShowModalAddCustomer(true);
                }}
                onDelete={() => {
                  setSelectedCustomer(c);
                  setShowModalDelete(true);
                }}
              />
            </Col>
          ))
        )}
      </Row>

      {!isLoadingFirst && !showEmpty && <div ref={sentinelRef} style={{ height: 1, width: "100%" }} />}

      {!isLoadingFirst && isLoadingMore && (
        <div style={{ padding: "12px 0" }}>
          <AppLoading />
        </div>
      )}

      {!isLoadingFirst && !isLoadingMore && !showEmpty && !hasMore && (
        <div style={{ textAlign: "center", padding: "10px 0 2px", color: "#6b7280", fontSize: 12 }}>
          Fim dos resultados
        </div>
      )}

      <FloatingVideoButton youtubeUrl={"https://www.youtube.com/embed/KZKgN3hYKd0"} />

      <FloatingButton
        icon={faAdd}
        onClick={() => {
          setSelectedCustomer({});
          setShowModalAddCustomer(true);
        }}
      />

      {showBackToTop && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          aria-label="Voltar ao topo"
          className="back-to-top back-to-top--visible"
        >
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      )}

      {modalHistoryCustomerShow && (
        <CustomerDetailsModalV2
          show={modalHistoryCustomerShow}
          customer={selectedCustomer}
          closeShow={() => {
            setModalHistoryCustomerShow(false);
            setSelectedCustomer({});
          }}
        />
      )}

      {showModalAddCustomer && (
        <AddManualCustomerModal
          show={showModalAddCustomer}
          customer={selectedCustomer}
          call={async () => {
            await resetAndLoad();
          }}
          closeShow={() => {
            setSelectedCustomer({});
            setShowModalAddCustomer(false);
          }}
        />
      )}

      {showModalImportCustomer && (
        <ImportCustomerModal
          show={showModalImportCustomer}
          call={async () => {
            await resetAndLoad();
          }}
          closeShow={() => {
            setShowModalImportCustomer(false);
          }}
        />
      )}

      {showModalDelete && (
        <ConfirmModal
          show={showModalDelete}
          title="Excluir"
          isLoading={isLoadingModalDelete}
          confirm={executeDeletion}
          closeModal={() => {
            setShowModalDelete(false);
            setSelectedCustomer({});
          }}
        >
          Deseja remover o(a) cliente{" "}
          <span style={{ fontWeight: "bold" }}>{selectedCustomer?.name}</span>?
        </ConfirmModal>
      )}

      <div style={{ height: "calc(50px + 72px)" }}></div>
    </div>
  );
}

export default Customers;
